import { Flex, VStack } from '@chakra-ui/react'
import { t } from 'i18next'
import { Voicemail as VoicemailIcon } from 'lucide-react'
import { concat, equals } from 'ramda'
import React, { useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import {
  ItemWrapper,
  UnreadWrapper,
} from '~/components/compositional/ListViewStyles'
import { FilterMenu, Text } from '~/components/core'
import { FilterOption } from '~/components/core/FilterMenu'
import { FontStyle } from '~/components/core/Text'
import EmptySearch from '~/components/EmptySearch'
import { EmptyState } from '~/components/EmptyState'
import ListItem from '~/components/ListItem'
import {
  DropdownSelect,
  SelectItem,
} from '~/components/shared/Dropdown/DropdownSelect'
import { MenuListItem } from '~/components/shared/Dropdown/MenuItem'
import {
  ScrollingList,
  ScrollToIndexParams,
} from '~/components/shared/ScrollingList'
import { SearchInput } from '~/components/shared/SearchInput'
import urls from '~/constants/urls'
import { ExtensionTypeValue } from '~/queries/extensionQueries'
import { UnreadVoicemailStatusFilter } from '~/types/ApiRequestTypes'
import { SearchValue } from '~/types/Shared'
import { hasValue } from '~/utils/common'
import { convertVoicemalLength, formatRelativeDay } from '~/utils/formatter'
import { openInNewTab } from '~/utils/functions'
import { findHighlightChunks } from '~/utils/highlightChunks'

import { Extension, Voicemail } from '@mangovoice/types'

import {
  SharedVMWrapper,
  SubtitleWrapper,
  TitleMatch,
} from './Voicemail.styles'

interface VoicemailListViewProps {
  voicemailList?: Voicemail[]
  listDataLoading?: boolean
  listDataFetching?: boolean
  selectedVoicemailId?: number
  selectedVoicemail?: Voicemail
  selectedExtension?: number | string
  voicemailExtensions?: Extension[]
  unreadFilter?: UnreadVoicemailStatusFilter
  searchValue?: SearchValue
  onVoicemailSelected?: (selectedVoicemail: Voicemail) => void
  onExtensionSelected?: (selectedExtension: string | number) => void
  onUnreadFilterChanged?: (newFilterValue: UnreadVoicemailStatusFilter) => void
  onSearchFocusedChanged?: (focused: boolean) => void
  onSearchChanged?: (newSearchValue: SearchValue) => void
  onNextPageRequested?: () => void
}

const listViewFilterOptions: () => FilterOption[] = () => [
  {
    label: t('voicemail.filter.all'),
    value: UnreadVoicemailStatusFilter.ALL,
    default: true,
  },
  {
    label: t('voicemail.filter.unread'),
    value: UnreadVoicemailStatusFilter.TRUE,
  },
]

export const VoicemailListView = ({
  voicemailList,
  listDataLoading,
  selectedExtension,
  voicemailExtensions,
  unreadFilter,
  searchValue = SearchValue.empty,
  onVoicemailSelected,
  onExtensionSelected,
  onSearchChanged,
  onUnreadFilterChanged,
  onNextPageRequested,
}: VoicemailListViewProps) => {
  const { voicemailId } = useParams()
  const { t } = useTranslation()
  const clickedVoicemailIdRef = useRef<number | undefined>(undefined)
  const routeScrollRef = useRef<
    { voicemailId: number; target?: ScrollToIndexParams } | undefined
  >(undefined)

  const routeVoicemailId = Number(voicemailId)
  const selectedVoicemailIndex =
    voicemailList?.findIndex(
      (voicemail) => voicemail.id === routeVoicemailId,
    ) ?? -1

  // Each route voicemail is resolved once: rows clicked here stay in place,
  // any other arrival (deep link, back/forward) scrolls its row to the top.
  if (voicemailId && routeScrollRef.current?.voicemailId !== routeVoicemailId) {
    if (clickedVoicemailIdRef.current === routeVoicemailId) {
      clickedVoicemailIdRef.current = undefined
      routeScrollRef.current = { voicemailId: routeVoicemailId }
    } else if (selectedVoicemailIndex >= 0) {
      routeScrollRef.current = {
        voicemailId: routeVoicemailId,
        target: {
          index: selectedVoicemailIndex,
          options: { align: 'start', behavior: 'auto' },
        },
      }
    }
  }
  const scrollToIndex =
    routeScrollRef.current?.voicemailId === routeVoicemailId
      ? routeScrollRef.current.target
      : undefined

  const handleClickItem = (item: Voicemail) => (e: React.MouseEvent) => {
    e.preventDefault()
    clickedVoicemailIdRef.current = item.id
    onVoicemailSelected?.(item)
  }

  const handleOpenSupport = () => {
    openInNewTab(urls.SUPPORT)
  }

  const handleExtensionSelected = (id: number | string, index: number) => {
    if (index === 0) {
      onExtensionSelected?.('')
      return
    }
    onExtensionSelected?.(id)
  }

  const extOptions =
    voicemailExtensions
      ?.filter((e) => e.voicemail_status)
      ?.map((extension) => {
        const { id, name, voicemail_status } = extension
        const unreadCount =
          voicemail_status && voicemail_status.unread_count > 999
            ? '999+'
            : (voicemail_status?.unread_count ?? 0)
        const unreadIndicator =
          unreadCount !== 0 ? (
            <UnreadWrapper>{unreadCount}</UnreadWrapper>
          ) : undefined
        const label = (
          <MenuListItem primaryText={name} rightContent={unreadIndicator} />
        )
        return { value: id, selectedLabel: name, content: label }
      }) ?? []

  const options = concat(
    [{ value: 0, content: t('voicemail.filter.allInboxes') }] as any,
    extOptions,
  ) as unknown as SelectItem<string | number>[]

  const listContent = useMemo(() => {
    if (voicemailList && voicemailList.length === 0 && !listDataLoading) {
      if (hasValue(searchValue.label)) {
        return <EmptySearch search={searchValue.value} />
      } else {
        return (
          //temp until rewritten - stretches the EmptyState box
          <Flex
            direction="column"
            height="100%"
            justifyContent="flex-start"
            alignItems="flex-start"
          >
            <EmptyState
              title={t('voicemail.emptyState.title')}
              subtitle={t('voicemail.emptyState.subtitle1')}
              subtitle2={t('voicemail.emptyState.subtitle2')}
              buttonText={t('voicemail.emptyState.buttonText')}
              onClick={handleOpenSupport}
            />
          </Flex>
        )
      }
    }

    return (
      <ScrollingList
        display="flex"
        flex="1"
        width="100%"
        height="70%"
        px=".25rem"
        py=".75rem"
        scrollToIndex={scrollToIndex}
        showLoaderFooter={listDataLoading}
        scrollbarOptions={{ showOnHoverOnly: true }}
        itemData={voicemailList ?? []}
        getItemKey={(voicemail) => voicemail.id}
        onEndReached={() => onNextPageRequested?.()}
        onBuildItemContent={(_, voicemail: Voicemail) => {
          const {
            id,
            caller_id_name,
            created_at,
            length_in_seconds,
            read_at,
            extension_id,
          } = voicemail

          const isSharedVoicemail =
            (voicemailExtensions?.find((ext) => ext.id === extension_id)
              ?.type as ExtensionTypeValue) === 'ExtensionSharedVoicemailBox'
          const voicemailName = caller_id_name
          const unRead = read_at === null
          const selected = equals(Number(voicemailId), id)
          const chunks = findHighlightChunks(voicemailName, searchValue.value)
          const voicemailBoxName =
            voicemailExtensions?.find((vm) => vm.id === extension_id)?.name ??
            ''
          const titleElement = (
            <Text
              as="span"
              fontStyle={FontStyle.Normal}
              color="var(--chakra-colors-fg-default)"
              weight={unRead ? 'bold' : 'normal'}
            >
              {hasValue(searchValue.value)
                ? chunks.map(({ start, end, highlight }) => {
                    const text = voicemailName.slice(start, end)
                    return highlight ? (
                      <TitleMatch key={start}>{text}</TitleMatch>
                    ) : (
                      <React.Fragment key={start}>{text}</React.Fragment>
                    )
                  })
                : voicemailName}
            </Text>
          )
          return (
            <ListItem
              key={`voicemail-${id}`}
              activated={selected}
              isUnread={unRead}
              title={titleElement}
              subTitle={
                <SubtitleWrapper>
                  {isSharedVoicemail && (
                    <SharedVMWrapper>
                      <VoicemailIcon size={16} />
                    </SharedVMWrapper>
                  )}
                  <Text
                    as="span"
                    fontStyle={FontStyle.Tiny}
                    text={voicemailBoxName}
                    color="var(--chakra-colors-fg-default)"
                  />
                </SubtitleWrapper>
              }
              date={
                <Text
                  as="span"
                  fontStyle={FontStyle.Small}
                  text={formatRelativeDay(created_at)}
                  color="var(--chakra-colors-fg-muted)"
                />
              }
              time={
                <Text
                  as="span"
                  fontStyle={FontStyle.Tiny}
                  text={convertVoicemalLength(length_in_seconds)}
                  color="var(--chakra-colors-fg-muted)"
                />
              }
              onClick={handleClickItem(voicemail)}
            />
          )
        }}
      />
    )
  }, [
    voicemailList,
    listDataLoading,
    searchValue,
    voicemailId,
    unreadFilter,
    scrollToIndex,
  ])

  return (
    <VStack display="flex" height="100%" alignSelf="stretch" gap="0">
      <Flex
        width="100%"
        padding="1rem"
        justifyContent="center"
        alignItems="center"
        gap="0.75rem"
        alignSelf="stretch"
        borderBottom="2px solid var(--chakra-colors-border-default)"
      >
        <SearchInput
          placeholder={t('voicemail.searchPlaceholder')}
          inputLabel={t('voicemail.searchLabel', 'Search voicemail')}
          onChange={onSearchChanged}
        />
      </Flex>
      {options.length > 2 && (
        <Flex
          width="100%"
          padding="1rem"
          justifyContent="center"
          alignItems="center"
          gap="0.75rem"
          alignSelf="stretch"
          backgroundColor="bg.subtle"
        >
          <DropdownSelect
            items={options}
            onSelect={handleExtensionSelected}
            selectedValue={!selectedExtension ? 0 : selectedExtension}
          />
        </Flex>
      )}

      <ItemWrapper $bottom>
        <FilterMenu
          selectedFilter={unreadFilter ?? UnreadVoicemailStatusFilter.ALL}
          options={listViewFilterOptions()}
          ariaLabel={t('a11y.filterGroup.voicemail')}
          onChange={(val) =>
            onUnreadFilterChanged?.(val as UnreadVoicemailStatusFilter)
          }
        />
      </ItemWrapper>
      {listContent}
    </VStack>
  )
}
